import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderOpen, faChevronRight } from '@fortawesome/free-solid-svg-icons';

import { getPacketKey } from '../fileFolderUtils';

const ARTIFACT_ORDER = {
  dbq_pdf: 0,
  medical_opinion_pdf: 1,
  medical_opinion_evidence_pdf: 2,
  musculoskeletal_supplemental_pdf: 3,
};

const GroupWrapper = styled.div`
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background-color: #f9fafb;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const Chevron = styled(FontAwesomeIcon)`
  font-size: 0.7rem;
  color: #999;
  transition: transform 0.15s;
  transform: ${(props) => (props.$expanded ? 'rotate(90deg)' : 'rotate(0deg)')};
`;

const GroupCount = styled.span`
  font-weight: 400;
  color: #999;
  text-transform: none;
  letter-spacing: normal;
  font-size: 0.8rem;
`;

const EmptyState = styled.div`
  padding: 1rem 0.75rem;
  color: #999;
  font-size: 0.875rem;
  font-style: italic;
`;

const prettifyPacketFolderName = (folderName) =>
  String(folderName || '')
    .replace(/-[a-f0-9]{6,8}$/i, '')
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'DBQ Request';

const getPacketLabel = (file) =>
  file?.fileMeta?.packetDisplayName ||
  prettifyPacketFolderName(file?.fileMeta?.packetFolderName);

const compareArtifacts = (a, b) => {
  const orderA = ARTIFACT_ORDER[a?.fileMeta?.artifactType] ?? 999;
  const orderB = ARTIFACT_ORDER[b?.fileMeta?.artifactType] ?? 999;
  if (orderA !== orderB) return orderA - orderB;
  return String(a.fileName || '').localeCompare(String(b.fileName || ''));
};

const PacketGroupList = ({ files, renderFileItem }) => {
  const groups = useMemo(() => {
    const map = new Map();
    files.forEach((file) => {
      const key = getPacketKey(file);
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: getPacketLabel(file),
          latestUpdate: file.fileLastUpdated || file.updatedAt || file.createdAt || null,
          files: [],
        });
      }
      const group = map.get(key);
      group.files.push(file);
      const candidate = file.fileLastUpdated || file.updatedAt || file.createdAt || null;
      if (candidate && (!group.latestUpdate || candidate > group.latestUpdate)) {
        group.latestUpdate = candidate;
      }
    });
    return Array.from(map.values())
      .map((group) => ({
        ...group,
        files: [...group.files].sort(compareArtifacts),
      }))
      .sort((a, b) => {
        if (a.latestUpdate && b.latestUpdate) {
          return b.latestUpdate.localeCompare(a.latestUpdate);
        }
        if (a.latestUpdate) return -1;
        if (b.latestUpdate) return 1;
        return a.label.localeCompare(b.label);
      });
  }, [files]);

  // Collapsed by default; click a group header to expand.
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (groups.length === 0) {
    return <EmptyState>No exam packets yet.</EmptyState>;
  }

  let draggableIndex = 0;

  return (
    <div>
      {groups.map((group) => {
        const isExpanded = expandedGroups.has(group.key);
        return (
          <GroupWrapper key={group.key}>
            <GroupHeader
              onClick={() => toggleGroup(group.key)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleGroup(group.key);
                }
              }}
              aria-expanded={isExpanded}
            >
              <Chevron icon={faChevronRight} $expanded={isExpanded} />
              <FontAwesomeIcon icon={faFolderOpen} />
              <span>{group.label}</span>
              <GroupCount>
                ({group.files.length} {group.files.length === 1 ? 'file' : 'files'})
              </GroupCount>
            </GroupHeader>
            {isExpanded
              ? group.files.map((file) => {
                  const index = draggableIndex;
                  draggableIndex += 1;
                  return (
                    <div key={file.fileId}>{renderFileItem(file, index)}</div>
                  );
                })
              : null}
          </GroupWrapper>
        );
      })}
    </div>
  );
};

export default PacketGroupList;
